import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  chakra,
  Flex,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { phoneFormatter } from "../../../shared/utils/phone-formatter";
import PatientDocumentSectionedIcdCodes from "../../PatientDocumentSectionedIcdCodes/PatientDocumentSectionedIcdCodes";
import PatientMedicalDiagnosisCodeSection from "../../patient/components/PatientMedicalDiagnosisCodeSection";
import PatientMedicationSection from "../../patient/components/PatientMedicationSection";
import PatientPrimaryPhysician from "../../patientPhysician/PatientPrimaryPhysician";
import {
  firstItemOfTypes,
  icdSnapshotQuestionId,
  icdViewSnapshot,
  MEDICATION_HEADINGS,
  medicalDiagnosisPrint,
  medicationPrint,
  medicationProfileText,
  NO_PHYSICIAN_TEXT,
  NOT_TAKING_MEDICATIONS_TEXT,
  PHYSICIAN_TITLE,
  physicianPrintLines,
  printsMedicationsAsText,
} from "../entity-editors/entityPrint";
import {
  PatientContractDiagnosisCodesEditor,
  PatientContractDiagnosisCodesView,
} from "../entity-editors/PatientContractDiagnosisCodesEditor";
import {
  type EntityEditorActivity,
  IDLE_ENTITY_ACTIVITY,
  useReviewEntity,
} from "../entity-editors/reviewEntityContext";
import { replacePlaceholders } from "../layout/documentLayout";
import { NO_CHART_ROW } from "./answerWrites";
import { BarTitle, MutedNote, PrintBlock, TitledBlock } from "./printStyles";
import { PrintTable } from "./TableQuestion";
import type { QuestionEditorProps, QuestionViewProps } from "./types";

// Questions backed by a patient record rather than their answer: medicationProfile (v2
// medications), icdCodes / finalIcdCodes (contract ICD codes), sectionedIcdCodes, medicalDiagnosis
// and patientPhysician. Views print the live record the way the PDF does (entityPrint.ts); editors
// embed the patient-record components, under a line saying they change the patient's record.

export const ENTITY_EDITOR_NOTICE = "Changes here update the patient's record";

/** Without the patient page's edit permission of a record, its editor only shows it. */
function NoEditPermissionNote(props: { what: string }) {
  return (
    <Text color="gray.500" fontSize="sm">
      You don&apos;t have permission to edit the patient&apos;s {props.what}.
    </Text>
  );
}

// ---- Views ----

function LoadingNote(props: { error: boolean; title: string; what: string }) {
  return (
    <TitledBlock gap="6px" isInline={false} title={props.title}>
      <MutedNote>
        {props.error ? `Could not load the patient's ${props.what}.` : `Loading ${props.what}...`}
      </MutedNote>
    </TitledBlock>
  );
}

export function MedicationProfileView({ block, layoutType, placeholders }: QuestionViewProps) {
  const { content, documentType, patientId } = useReviewEntity();
  const { queries } = useApi();
  const medications = useQuery(queries.patient.medications(patientId));
  const { question } = block;
  const title = replacePlaceholders(question.label, placeholders);

  if (medications.status !== "success") {
    return <LoadingNote error={medications.status === "error"} title={title} what="medications" />;
  }

  if (printsMedicationsAsText(documentType, content, question.id)) {
    // Printed as a textLong answer, one line per medication
    return (
      <TitledBlock gap="6px" isInline={question.answerInline === true} title={title}>
        <chakra.div whiteSpace="pre-wrap">
          {medicationProfileText({
            medications: medications.data.medications,
            notTakingMedications: medications.data.notTakingMedications,
          })}
        </chakra.div>
      </TitledBlock>
    );
  }

  const printed = medicationPrint({
    isFirstMedicationProfile: firstItemOfTypes(content, ["medicationProfile"])?.id === question.id,
    medications: medications.data.medications,
    notTakingMedications: medications.data.notTakingMedications,
  });
  switch (printed.type) {
    case "notTaking":
      // Printed as a textLong answer
      return (
        <TitledBlock gap="6px" isInline={question.answerInline === true} title={title}>
          <chakra.div whiteSpace="pre-wrap">{NOT_TAKING_MEDICATIONS_TEXT}</chakra.div>
        </TitledBlock>
      );
    case "none":
      // The GENERAL preview mirrors the PDF, which prints nothing here; the template list says why
      if (layoutType === "preview") {
        return null;
      }
      return (
        <TitledBlock gap="6px" isInline={false} title={title}>
          <MutedNote>
            Not printed: the patient is not taking any medications (the first medication profile
            says so).
          </MutedNote>
        </TitledBlock>
      );
    case "table":
      return <PrintTable headings={MEDICATION_HEADINGS} rows={printed.rows} title={title} />;
  }
}

export function IcdCodesView({ answers, block, layoutType, placeholders }: QuestionViewProps) {
  const { content, patientContractId, patientId } = useReviewEntity();
  const { question } = block;

  return (
    <PatientContractDiagnosisCodesView
      layout={layoutType}
      patientContractId={patientContractId}
      patientId={patientId}
      snapshot={icdViewSnapshot(question, content, answers)}
      title={replacePlaceholders(question.label, placeholders)}
      // A GENERAL document's icdCodes is printed as a chart (recursiveFillICDItemPlaceholderPDF)
      variant={layoutType === "preview" && question.itemType === "icdCodes" ? "chart" : "table"}
    />
  );
}

export function MedicalDiagnosisView({ block, placeholders }: QuestionViewProps) {
  const { patientId } = useReviewEntity();
  const { queries } = useApi();
  const codes = useQuery(queries.patient.medicalDiagnosisCodes(patientId));
  const title = replacePlaceholders(block.question.label, placeholders);

  if (codes.status !== "success") {
    return (
      <LoadingNote error={codes.status === "error"} title={title} what="medical diagnosis codes" />
    );
  }
  const printed = medicalDiagnosisPrint(codes.data);
  return <PrintTable headings={printed.headings} rows={printed.rows} title={title} />;
}

/** The server prints an E.164 number nationally formatted, anything else as is. */
function formatPhysicianPhone(phone: string): string {
  return phoneFormatter.isValidNumber(phone) && phoneFormatter.formatE164(phone) === phone
    ? phoneFormatter.formatNational(phone)
    : phone;
}

export function PatientPhysicianView() {
  const { patientId } = useReviewEntity();
  const { queries } = useApi();
  const physician = useQuery(queries.patientPhysician.getPrimaryPhysician(patientId));

  if (physician.status !== "success") {
    return (
      <LoadingNote
        error={physician.status === "error"}
        title={PHYSICIAN_TITLE}
        what="primary physician"
      />
    );
  }
  if (physician.data.physician === null) {
    return (
      <TitledBlock gap="6px" isInline={false} title={PHYSICIAN_TITLE}>
        <div>{NO_PHYSICIAN_TEXT}</div>
      </TitledBlock>
    );
  }
  return (
    <PrintBlock display="flex" flexDirection="column" gap="8px">
      <BarTitle>{PHYSICIAN_TITLE}</BarTitle>
      {physicianPrintLines(physician.data.physician, formatPhysicianPhone).map((line) => (
        <chakra.div key={line.key} display="flex" gap="8px">
          <chakra.strong flexShrink={0} fontWeight={600}>
            {line.key}:
          </chakra.strong>
          <span>{line.value}</span>
        </chakra.div>
      ))}
    </PrintBlock>
  );
}

// ---- Editors ----

/**
 * Frame of every entity editor: the question, the "patient's record" notice, the record's own
 * component, and Done (disabled while one of its forms is open or a change is saving). Reports
 * that activity to the review's close guard.
 */
function EntityEditorFrame(props: {
  children: (report: {
    onChanged: () => void;
    onFormOpenChange: (isOpen: boolean) => void;
    onSavingChange: (isSaving: boolean) => void;
  }) => React.ReactNode;
  editor: QuestionEditorProps;
}) {
  const entity = useReviewEntity();
  const { question } = props.editor;
  const [activity, setActivity] = React.useState<EntityEditorActivity>(IDLE_ENTITY_ACTIVITY);

  const { onActivityChange } = entity;
  React.useEffect(() => {
    onActivityChange(activity);
  }, [activity, onActivityChange]);
  React.useEffect(() => () => onActivityChange(IDLE_ENTITY_ACTIVITY), [onActivityChange]);

  const { onEntityChanged } = entity;
  const report = React.useMemo(
    () => ({
      onChanged: () => onEntityChanged(question.id),
      onFormOpenChange: (hasOpenForm: boolean) =>
        setActivity((prev) => (prev.hasOpenForm === hasOpenForm ? prev : { ...prev, hasOpenForm })),
      onSavingChange: (isSaving: boolean) =>
        setActivity((prev) => (prev.isSaving === isSaving ? prev : { ...prev, isSaving })),
    }),
    [onEntityChanged, question.id]
  );

  const isBusy = activity.hasOpenForm || activity.isSaving || props.editor.isSaving;

  return (
    <Box
      bg="white"
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      display="flex"
      flexDirection="column"
      gap={3}
      p={3}
    >
      <Text fontSize="sm" fontWeight="semibold">
        {replacePlaceholders(question.label, props.editor.placeholders)}
      </Text>
      <Alert borderRadius="md" fontSize="sm" py={2} status="info">
        <AlertIcon />
        <AlertDescription>{ENTITY_EDITOR_NOTICE}</AlertDescription>
      </Alert>

      {props.children(report)}

      <Flex alignItems="center" gap={2} justifyContent="flex-end">
        {activity.hasOpenForm && (
          <Text color="gray.500" fontSize="xs">
            Save or cancel the open form first
          </Text>
        )}
        <Button
          colorScheme="blue"
          isDisabled={isBusy}
          size="sm"
          onClick={() => {
            if (!isBusy) {
              props.editor.onClose();
            }
          }}
        >
          Done
        </Button>
      </Flex>
    </Box>
  );
}

export function MedicationProfileEditor(props: QuestionEditorProps) {
  const { patientId } = useReviewEntity();
  // The permission the patient page's medication profile uses for changes
  const canEdit = auth.isAllowedTo("edit_patient_page_medication_profile");
  return (
    <EntityEditorFrame editor={props}>
      {(report) => (
        <>
          <PatientMedicationSection
            isReadOnly={!canEdit}
            patientId={patientId}
            onChanged={report.onChanged}
            onFormOpenChange={report.onFormOpenChange}
            onSavingChange={report.onSavingChange}
          />
          {!canEdit && <NoEditPermissionNote what="medications" />}
        </>
      )}
    </EntityEditorFrame>
  );
}

export function IcdCodesEditor(props: QuestionEditorProps) {
  const { content, patientContractId, patientId } = useReviewEntity();
  const snapshotQuestionId = icdSnapshotQuestionId(content);
  const snapshotQuestion =
    snapshotQuestionId === null
      ? undefined
      : {
          id: snapshotQuestionId,
          chartRow: props.answers.get(snapshotQuestionId)?.[0]?.chartRow ?? NO_CHART_ROW,
        };
  const { onSaveAnswers } = props;

  return (
    <EntityEditorFrame editor={props}>
      {(report) => (
        <PatientContractDiagnosisCodesEditor
          patientContractId={patientContractId}
          patientId={patientId}
          snapshotQuestion={snapshotQuestion}
          onChanged={report.onChanged}
          onFormOpenChange={report.onFormOpenChange}
          onSavingChange={report.onSavingChange}
          // Saved as an answer edit too: the PDF prints this snapshot (see icdSnapshot.ts)
          onSnapshotWrite={(write) => onSaveAnswers([write])}
        />
      )}
    </EntityEditorFrame>
  );
}

export function SectionedIcdCodesEditor(props: QuestionEditorProps) {
  const { scheduledDocId } = useReviewEntity();
  const { htmlTemplateId } = props.question;

  return (
    <EntityEditorFrame editor={props}>
      {(report) =>
        scheduledDocId === null || htmlTemplateId === undefined ? (
          <Text color="gray.500" fontSize="sm">
            This question has no document section to resolve.
          </Text>
        ) : (
          <Box>
            <Text color="gray.600" fontSize="sm">
              Resolving marks this section as having no relevant diagnosis codes.
            </Text>
            <PatientDocumentSectionedIcdCodes
              documentId={scheduledDocId}
              htmlTemplateId={htmlTemplateId}
              onChanged={report.onChanged}
              onSavingChange={report.onSavingChange}
            />
          </Box>
        )
      }
    </EntityEditorFrame>
  );
}

export function MedicalDiagnosisEditor(props: QuestionEditorProps) {
  const { patientId } = useReviewEntity();
  // The patient page's diagnosis codes permission (its "Medical Diagnosis Codes" section is
  // gated by view_patient_page_diagnosis_codes; the ICD editor uses the same edit key)
  const canEdit = auth.isAllowedTo("edit_patient_page_diagnosis_codes");
  return (
    <EntityEditorFrame editor={props}>
      {(report) => (
        <>
          <PatientMedicalDiagnosisCodeSection
            isReadOnly={!canEdit}
            patientId={patientId}
            onChanged={report.onChanged}
            onFormOpenChange={report.onFormOpenChange}
          />
          {!canEdit && <NoEditPermissionNote what="medical diagnosis codes" />}
        </>
      )}
    </EntityEditorFrame>
  );
}

export function PatientPhysicianEditor(props: QuestionEditorProps) {
  const { patientId } = useReviewEntity();
  // The permission of the patient page's Physicians section (patient-physicians.html)
  const canEdit = auth.isAllowedTo("edit_patient_physicians");
  return (
    <EntityEditorFrame editor={props}>
      {(report) => (
        <>
          <PatientPhysicianView />
          {canEdit ? (
            <PatientPrimaryPhysician
              isEdit={true}
              patientId={patientId}
              onChanged={report.onChanged}
              onSavingChange={report.onSavingChange}
            />
          ) : (
            <NoEditPermissionNote what="primary physician" />
          )}
        </>
      )}
    </EntityEditorFrame>
  );
}
